<script setup lang="ts">
import { Question } from "../components/fields";
import Input from "../components/fields/Input.vue";
import { loginConfirmValidator as validator } from "../utils/validators";
import useVuelidate from "@vuelidate/core";
import { LOGIN_KEY, ResponseCodeMap, ResponseCodes, SESSION_EXPIRY, useLoginStore } from "../stores/login";
import { ToastRenderer, useToast } from "../services/toast";
import { confirmMagicToken, getMyQuotes } from "../data/login";
import { getLatestPartialFormRequest } from "../data/apply";
import { useRouter } from "vue-router";
import { useConfigStore } from "../stores/config";
import { mapDataFromQuoteRequest } from "../utils/quote";
import { useQuoteStore } from "@/stores/quotes";
import { captureException } from "@sentry/vue";
import { handleApiResponse } from "@/utils/api";
import { z } from "zod";

const config = useConfigStore();
const { toast } = useToast();
const router = useRouter();
const loginStore = useLoginStore();
const quoteStore = useQuoteStore();
const v$ = useVuelidate(validator, loginStore);

function showToast(message: string) {
    toast({
        title: "Error",
        message,
        timeout: config.toastTimeout,
    });
}

function checkLoginAttempts() {
    const session = sessionStorage.getItem(LOGIN_KEY);
    const jsonSession = JSON.parse(session || "");
    if ((session && jsonSession?.loginAttempts >= 4) || loginStore.loginAttempts >= 4) {
        backToLogin();
    }
}

function getCodeVerifier() {
    return loginStore.codeVerifier;
}

function updateLoginAttemptAndSession() {
    const loginSession = sessionStorage.getItem(LOGIN_KEY);
    if (loginSession) {
        const session = JSON.parse(loginSession);
        session.loginAttempts = session.loginAttempts + 1;
        session.expiry = SESSION_EXPIRY;
        sessionStorage.setItem(LOGIN_KEY, JSON.stringify(session));
        loginStore.loginAttempts = loginStore.loginAttempts + 1;
    } else {
        loginStore.loginAttempts = 1;
        sessionStorage.setItem(LOGIN_KEY, JSON.stringify({
            codeVerifier: "",
            email: "",
            loginAttempts: 1,
            expiry: 0,
            isLoggedIn: false,
        }));
    }
}

function backToLogin() {
    loginStore.logout();
    router?.replace({ path: "/login" });
}

async function handleLogin(token: string) {
    const quotes = await getMyQuotes(loginStore.email, token);
    if (quotes.data.response.quotes?.length > 0) {
        const quoteResponse = quotes.data.response.quotes[0];
        quoteResponse.quote.async = true;
        quoteStore.setToken(quoteResponse.quoteNumber, token);
        quoteStore.quotations = quoteResponse.quotations;
        quoteStore.panelSize = quoteResponse.quotationsCount;
        quoteStore.quote = quoteResponse.quote;
        // Use window.location.href to trigger full page reload for server-side cookie setting
        window.location.href = `/results?quotenumber=${quoteResponse.quoteNumber}`;
    } else {
        const partialResponse = await getLatestPartialFormRequest(loginStore.email, token);
        mapDataFromQuoteRequest(partialResponse);
        router.push({ path: "/" });
    }
}

async function handleSubmitClick() {
    const valid = await v$.value.$validate();
    if (valid) {
        updateLoginAttemptAndSession();
        checkLoginAttempts();
        try {
            const response = await confirmMagicToken(loginStore.email, getCodeVerifier(), loginStore.code);
            const status = response.status;

            if (status === ResponseCodes.Ok) {
                const successSchema = z.object({
                    response: z.object({
                        success: z.boolean(),
                        token: z.string(),
                    }),
                });
                type SuccessResponse = z.infer<typeof successSchema>;
                const validated = handleApiResponse<SuccessResponse>(
                    response.data, successSchema, "confirmMagicToken",
                );
                loginStore.login();
                await handleLogin(validated.response.token);
                return;
            }

            switch (true) {
                case (status === 403):
                    showToast(ResponseCodeMap[ResponseCodes.FailedCode]);
                    break;
                case (status === 429):
                    showToast(ResponseCodeMap[ResponseCodes.TooManyRequests]);
                    break;
                case (status === 400):
                    showToast(ResponseCodeMap[ResponseCodes.BadRequest]);
                    break;
                case (status > 400 && status < 500):
                    showToast(ResponseCodeMap[ResponseCodes.Problem]);
                    break;
                case (status === 500):
                    showToast(ResponseCodeMap[ResponseCodes.Error]);
                    break;
            }
        } catch (err) {
            captureException(err);
            showToast(ResponseCodeMap[ResponseCodes.Error]);
        }
    }
}

</script>
<template>
    <div class="container col-lg-6 col-md-6 mx-auto login">
        <h1 class="mt-4">
            Login by Email
        </h1>
        <h5 class="pt-3 pb-3 blue">
            We've sent an 8 digit one-time verification code to
            <br><br>
            <span class="sessioncamhidetext">{{ loginStore.email }}</span>.
            <br><br>
            Please retrieve that code and type it into the box below to login.
        </h5>
        <Question
            name="code"
            label="Enter your one-time verification code here."
            :full-width="true"
        >
            <Input
                id="login-code"
                v-model="loginStore.code"
                class="col-12"
                placeholder="________"
                type="number"
                :validation="validator.code"
            />
        </Question>
        <button
            id="btn-submit"
            class="btn btn-info btn-lg mt-2"
            @click.prevent="handleSubmitClick"
        >
            Submit
        </button>
        <hr>
        <p>
            Not received your code? <a
                id="login-new-code"
                href="#"
                @click.prevent="backToLogin"
            >Click here to receive a new one</a>.
        </p>
    </div>
    <ToastRenderer />
</template>
